import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Lock, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getBranding } from '@/lib/api';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const loginSchema = z.object({
  username: z.string().min(1, { message: 'Benutzername erforderlich' }),
  password: z.string().min(1, { message: 'Passwort erforderlich' }),
});

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({ app_name: 'UN-IT Forms' });

  useEffect(() => {
    getBranding().then(setBranding).catch(() => {});
  }, []);

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const getLogoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

/*  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const result = await login(values.username, values.password);
      if (result.must_change_password) {
        toast.info('Bitte ändern Sie Ihr Passwort');
        navigate('/admin/change-password');
      } else {
        toast.success('Erfolgreich angemeldet');
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };*/

 const onSubmit = async (values) => {
  setLoading(true);
  try {
    await login(values.username, values.password);

    toast.success('Erfolgreich angemeldet');
    navigate('/admin');
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
  } finally {
    setLoading(false);
  }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">

      {/* LEFT – LOGIN */}
      <div className="flex items-center justify-center px-8 bg-gradient-to-br from-[#faf9f7] to-[#f1eee9]">
        <div className="w-full max-w-sm animate-fade-in">

          {/* Header */}
          <div className="text-center mb-10">
            {branding.app_logo_light ? (
              <img
                src={getLogoUrl(branding.app_logo_light)}
                alt={branding.app_name}
                className="h-16 mx-auto mb-5 drop-shadow-sm transition-transform duration-300 hover:scale-[1.03]"
              />
            ) : (
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                {branding.app_name}
              </h1>
            )}

            <div className="w-14 h-[2px] bg-[#b4a07b] mx-auto my-4 rounded-full" />

            <p className="text-slate-600 text-sm">
              Sicherer Zugriff auf das Verwaltungssystem
            </p>
          </div>

          {/* Glass Card */}
          <Card className="
            bg-white/70 backdrop-blur-xl
            border border-white/60
            shadow-xl rounded-2xl
          ">
            <CardContent className="p-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Benutzername</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                              placeholder="admin"
                              className="pl-10 bg-white/80"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Passwort</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                              type="password"
                              placeholder="••••••••"
                              className="pl-10 bg-white/80"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="
                      w-full h-11 mt-6 rounded-xl
                      bg-[#b4a07b]
                      hover:bg-[#a18d67]
                      active:bg-[#8f7c59]
                      text-white font-medium
                      shadow-lg shadow-[#b4a07b]/30
                      transition-all duration-200
                    "
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Anmelden…
                      </>
                    ) : (
                      'Anmelden'
                    )}
                  </Button>

                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center mt-8 space-y-1">
            <p className="text-xs text-slate-400 italic">
              Güven, emanet bilinciyle başlar.
            </p>
            <p className="text-[11px] text-slate-300">
              Vertrauen beginnt mit Verantwortung.
            </p>
            <p className="text-[10px] text-slate-300 mt-2">
              UN-IT Forms v2026.01.27
            </p>
          </div>

        </div>
      </div>

      {/* RIGHT – VISUAL */}
      <div
        className="hidden lg:block bg-cover bg-center relative"
        style={{ backgroundImage: `url('/login-bg.jpg')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        <div className="relative h-full flex items-end p-14">
          <div className="text-white max-w-lg">
            <h2 className="font-heading text-4xl font-bold mb-3 leading-tight">
              UN-IT Forms-Verwaltung
            </h2>
            <p className="text-lg text-white/90">
              “Allah adaletle davrananları sever.”
            </p>
            <p className="text-sm text-white/70 mt-1">
              Allah liebt diejenigen, die gerecht handeln.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}
